import React, { useState, useEffect } from "react";
import { X, Layers } from "lucide-react";
import "./AddScriptModal.css";
import { toast } from "react-hot-toast";
import AuthService from "../../services/Auth.service";
import AceEditor from "react-ace";

// Ace Editor setup
import "ace-builds/src-noconflict/mode-markdown";
import "ace-builds/src-noconflict/theme-github";

const AddScriptModal = ({ isOpen, onClose, companyId, campaignId, onSave, scriptToEdit}) => {
  const [script_name, setScriptName] = useState("");
  const [script_description, setScriptDescription] = useState("");
  const [greeting, setGreeting] = useState("");
  const [holdLine, setHoldLine] = useState(0);  
  const [script_content, setScriptContent] = useState("");
  const [additionalFields, setAdditionalFields] = useState([{ key: "", value: "" }]);

  const [activeMainTab, setActiveMainTab] = useState("scriptContent");
  const [loading, setLoading] = useState(false);

 useEffect(() => {
  if (!isOpen) return;

  const loadScript = async () => {
    // ✏️ EDIT MODE
    if (scriptToEdit) {
      try {
        setLoading(true);
        const res = await AuthService.getScriptById(scriptToEdit.id);
        const data = res?.data || res || {};

        setScriptName(data.script_name || "");
        setScriptDescription(data.script_description || "");
        setGreeting(data.greeting || "");
        setHoldLine(data.hold_line || "");
        setScriptContent(data.script_content || "");

        setAdditionalFields(
          data.additional_fields
            ? Object.entries(data.additional_fields).map(([key, value]) => ({
                key,
                value,
              }))
            : [{ key: "", value: "" }]
        );
      } catch (err) {
        console.error("Failed to load script", err);
      }
      return;
    }
    resetForm();
  };

  loadScript();
  setLoading(false);
}, [isOpen, scriptToEdit]);


const resetForm = () => {
  setScriptName("");
  setScriptDescription("");
  setGreeting("");
  setHoldLine("");
  setScriptContent("");
  setAdditionalFields([{ key: "", value: "" }]);
};


  const handleSave = () => {
    if (!script_name.trim()) {
      toast.error("Script name is required");
      return;
    }

    const payload = {
      script_name: script_name.trim(),
      script_description: script_description.trim(),
      greeting: greeting.trim(),
     hold_line: Number(holdLine),   // ✅ send as integer
      script_content: script_content.trim(),
      additional_fields: additionalFields.reduce((obj, item) => {
        if (item.key.trim() && item.value.trim()) {
          obj[item.key.trim()] = item.value.trim();
        }
        return obj;
      }, {}),
      company_id: companyId,
      campaign_id: campaignId,
    };

    onSave(payload);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="add-script-modal-content">

        {/* HEADER */}
        <div className="modal-header">
          <h2><Layers size={20} /> {scriptToEdit ? "Edit Script" : "Create New Script"}</h2>
          <button onClick={onClose} className="modal-close-btn"><X size={22} /></button>
        </div>

        {/* BODY */}
        <div className="modal-body">

          {/* SIDEBAR */}
          <div className="modal-sidebar">

            <label className="modal-label">Script Name</label>
            <input
              type="text"
              placeholder="Enter script name"
              value={script_name}
              onChange={(e) => setScriptName(e.target.value)}
              className="modal-input"
            />

            <label className="modal-label">Description</label>
            <textarea
              placeholder="Enter description"
              value={script_description}
              onChange={(e) => setScriptDescription(e.target.value)}
              className="modal-input"
            />

            <label className="modal-label">Greeting</label>
            <textarea
              placeholder="Hello! Thank you for calling..."
              value={greeting}
              onChange={(e) => setGreeting(e.target.value)}
              className="modal-input"
              style={{ height: "160px" }}
            />

            {/*  NEW FIELD */}
            <label className="modal-label">Hold Line</label>
            <textarea
                placeholder="Please hold the line..."
                value={holdLine}
                onChange={(e) => setHoldLine(parseInt(e.target.value) || 0)}
                className="modal-input"
                style={{ height: "120px" }}
              />

          </div>

          {/* MAIN CONTENT (Tabs) */}
          <div className="modal-main">

            <div className="main-tabs">
              <button
                className={`main-tab-btn ${activeMainTab === "scriptContent" ? "active" : ""}`}
                onClick={() => setActiveMainTab("scriptContent")}
              >
                Script Content
              </button>

              <button
                className={`main-tab-btn ${activeMainTab === "additionalFields" ? "active" : ""}`}
                onClick={() => setActiveMainTab("additionalFields")}
              >
                Additional Fields
              </button>
            </div>

            {activeMainTab === "scriptContent" && (
              <AceEditor
                mode="markdown"
                theme="github"
                width="100%"
                height="450px"
                fontSize={14}
                value={script_content}
                onChange={(val) => setScriptContent(val)}
                showPrintMargin={false}
                editorProps={{ $blockScrolling: true }}
                setOptions={{
                  useWorker: false,
                  showLineNumbers: true,
                }}
              />
            )}

            {activeMainTab === "additionalFields" && (
              <>
                <label className="modal-label" style={{ fontWeight: 700 }}>Additional Fields</label>

                {additionalFields.map((item, index) => (
                  <div key={index} style={{ display: "flex", gap: "6px", marginBottom: "8px" }}>
                    <input
                      type="text"
                      placeholder="key (customer_name)"
                      value={item.key}
                      disabled
                      onChange={(e) => {
                        const updated = [...additionalFields];
                        updated[index].key = e.target.value;
                        setAdditionalFields(updated);
                      }}
                      className="modal-input"
                      style={{ width: "50%" }}
                    />

                    <input
                      type="text"
                      placeholder="Value"
                      value={item.value}
                      onChange={(e) => {
                        const updated = [...additionalFields];
                        updated[index].value = e.target.value;
                        setAdditionalFields(updated);
                      }}
                      className="modal-input"
                      style={{ width: "50%" }}
                    />
                  </div>
                ))}

              </>
            )}
          </div>
        </div>

        {/* FOOTER */}
        <div className="modal-footer">
          <button className="modal-btn cancel" onClick={onClose}>Cancel</button>
          <button className="modal-btn save" onClick={handleSave}>
            {scriptToEdit ? "Update Script" : "Save Script"}
          </button>
        </div>

      </div>
    </div>
  );
};

export default AddScriptModal;
